import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import {memoize, orderBy} from "lodash";
import {createSelector} from "reselect";

import {fetchSelectors} from "@atg-shared/fetch-redux";
import {serverTime} from "@atg-shared/server-time";
import {
    liveOfferingStatuses,
    preLiveOfferingStatuses,
} from "@atg-sport-shared/pool-types";
import {OfferingStatus} from "@atg-sport-shared/pool-types/offeringTypes";
import {BetSelection} from "@atg-sport-shared/pool-types/selectionTypes";
import {getSport, getGameType, Pool, PoolSports} from "@atg-sport-shared/pool-util-pool";

import {
    findClosestSellOpenOffering,
    findLatestFinalizedOffering,
    findLatestSellClosedOffering,
    getHasOfferingPassed,
    getIsFinalizedOrCanceledOffering,
    getOfferingsByTypeAndSport,
    matchesGameType,
    sortOfferings,
} from "./offeringsHelpers";

import type {GameType, GlobalBig9State} from "@atg-sport-shared/pool-types";
import type {MatchResult} from "@atg-sport-shared/pool-types/matchTypes";
import type {
    MostPlayed,
    Offering,
    OfferingWithMatchRefs,
    OfferingWithMatches,
    OfferingsById,
} from "@atg-sport-shared/pool-types/offeringTypes";
import type {Big9RouteState} from "@atg-sport-shared/pool-types/routingState";
import type {Selector} from "reselect";

dayjs.extend(minMax);

type OfferingsWithMatchesByKey = {
    [key: string]: OfferingWithMatches;
};

export const getOfferingsState = (state: GlobalBig9State) =>
    state.sportsbook.big9.offerings;

const getMatchesState = (state: GlobalBig9State) => state.sportsbook.big9.matches ?? {};

export const getActiveOfferingType = createSelector(
    getOfferingsState,
    (offerings) => offerings.activeOfferingType,
);

export const getOfferingsStateByType = memoize(
    (type?: GameType | Pool, sport?: PoolSports | string) =>
        createSelector(getOfferingsState, (offerings) => {
            if (!type) {
                return offerings;
            }

            const filteredOfferingsById = Object.fromEntries(
                Object.entries(offerings.byId).filter(
                    ([_, offering]) =>
                        matchesGameType(offering.gameType, type) &&
                        (sport === undefined || offering.sport === sport),
                ),
            );

            // if no sport is provided, default to Football until app has support for sp5 hockey
            const filteredOfferingAllIds = offerings.allIds.filter(
                (id) =>
                    matchesGameType(offerings.byId[id].gameType, type) &&
                    offerings.byId[id].sport === (sport ?? PoolSports.Football),
            );

            return {
                ...offerings,
                byId: filteredOfferingsById,
                allIds: filteredOfferingAllIds,
            };
        }),
    (type, sport) => `${type}-${sport || ""}`,
);

export const getCompletedOfferings = (
    type?: Pool | GameType,
    sport?: PoolSports | string,
) =>
    createSelector(getOfferingsStateByType(type, sport), (offerings) =>
        orderBy(
            offerings.allIds
                .map((offeringId) => offerings.byId[offeringId])
                .filter(getHasOfferingPassed),
            ["startTime", "serialNumber"],
            ["desc", "desc"],
        ),
    );

export const calendarOfferings = (
    type?: Pool | GameType | undefined,
    sport?: PoolSports | string,
) =>
    createSelector(getOfferingsStateByType(type, sport), (offerings) => {
        const offeringsToMap = offerings.allIds.map(
            (offeringId) => offerings.byId[offeringId],
        );

        const allExceptUpcomingOfferings = orderBy(
            offeringsToMap.filter(
                (offering): boolean => offering?.status !== OfferingStatus.UPCOMING,
            ),
            ["startTime", "serialNumber"],
            ["desc", "desc"],
        );

        return allExceptUpcomingOfferings.slice(0, type === Pool.Big9 ? 6 : 10);
    });

export const getSellClosedOfferings = createSelector(getOfferingsState, (offerings) => {
    const ongoingOfferings = orderBy(
        offerings.allIds
            .map((offeringId) => offerings.byId[offeringId])
            .filter(
                (offering: OfferingWithMatchRefs | null): boolean =>
                    offering !== null && liveOfferingStatuses.includes(offering.status),
            ),
        ["startTime", "serialNumber"],
        ["desc", "desc"],
    );

    return ongoingOfferings.length === 0 ? null : ongoingOfferings;
});

export const getOngoingOfferings = createSelector(getOfferingsState, (offerings) => {
    const ongoingOfferings = orderBy(
        offerings.allIds
            .map((offeringId) => offerings.byId[offeringId])
            .filter(
                (offering: OfferingWithMatchRefs | null): boolean =>
                    offering !== null &&
                    preLiveOfferingStatuses.includes(offering.status),
            ),
        ["startTime", "serialNumber"],
        ["desc", "desc"],
    );

    return ongoingOfferings.length === 0 ? null : ongoingOfferings;
});

export const getClosestSellOpenOffering = (
    allIds?: string[],
    byId?: OfferingsById,
    type?: GameType,
) => {
    if (!allIds || !byId) return null;

    const offerings = sortOfferings(allIds, byId);

    if (!offerings || offerings.length === 0) return null;

    const filteredOfferings = type
        ? offerings.filter((offering) => offering.gameType === type)
        : offerings;

    if (filteredOfferings.length === 0) return null;

    return findClosestSellOpenOffering(filteredOfferings) ?? null;
};

export const getOfferingStatusUrlPath = (status: OfferingStatus) => {
    if (preLiveOfferingStatuses.includes(status)) return "spela";
    if (liveOfferingStatuses.includes(status)) return "live";
    return "resultat";
};

export const getUpcomingOfferings = (
    type: Pool | undefined,
    sport?: PoolSports | string | undefined,
) =>
    createSelector(getOfferingsStateByType(type, sport), (offerings) => {
        const filteredOfferings = orderBy(
            offerings.allIds.map((offeringId) => offerings.byId[offeringId]),
            ["startTime", "serialNumber"],
            ["asc", "desc"],
        ).filter((offering) => offering.status === OfferingStatus.SELL_OPEN);

        return filteredOfferings.length > 0 ? filteredOfferings : null;
    });

export const getOfferingsById = createSelector(
    getOfferingsState,
    (offerings): OfferingsById => offerings.byId,
);

export const getOfferingById = (
    offeringId: Offering["id"],
): Selector<GlobalBig9State, OfferingWithMatchRefs> =>
    createSelector(
        getOfferingsState,
        (offerings): OfferingWithMatchRefs => offerings.byId[offeringId],
    );

export const getOfferingIds = createSelector(
    getOfferingsState,
    (offerings): string[] => offerings.allIds,
);

export const getOfferingsByIdWithMatches: Selector<
    GlobalBig9State,
    OfferingsWithMatchesByKey
> = createSelector(getOfferingsById, getMatchesState, (offerings, matches) =>
    Object.keys(offerings).reduce((previous, offeringId) => {
        const offering = offerings[offeringId];
        const {overUnderLimit} = offering;

        return {
            ...previous,
            [offeringId]: {
                ...offering,
                matches: offering.matches.map(({id, number}) => {
                    const match = matches[id];

                    if (!match) return {};

                    return {
                        ...match,
                        matchId: id,
                        number,
                        overUnderLimit,
                        homeTeamName: match.homeTeam.shortName,
                        awayTeamName: match.awayTeam.shortName,
                    };
                }),
            },
        };
    }, {}),
);

export const getOfferingByPathname = createSelector(
    (state: GlobalBig9State) => state.router?.location?.pathname,
    getOfferingIds,
    getOfferingsById,
    (pathname, allIds, byId) => {
        const offerings = sortOfferings(allIds, byId);

        if (!offerings) return null;

        const useFinalizedOffering = pathname?.endsWith("/resultat");

        if (useFinalizedOffering) {
            const latestFinalizedOffering = findLatestFinalizedOffering(offerings);

            if (latestFinalizedOffering) return latestFinalizedOffering;
        }

        const useSellClosedOffering = pathname?.endsWith("/live");

        if (useSellClosedOffering) {
            const latestSellClosedOffering = findLatestSellClosedOffering(offerings);

            if (latestSellClosedOffering) return latestSellClosedOffering;
        }

        const closestSellOpenOffering = findClosestSellOpenOffering(offerings);

        return closestSellOpenOffering ?? offerings[0];
    },
);

export const getHeroOffering = (type?: Pool | GameType, sport?: PoolSports | string) =>
    createSelector(getOfferingsStateByType(type, sport), ({allIds, byId}) => {
        const offerings = sortOfferings(allIds, byId);

        if (!offerings) return null;

        const sellClosedOffering = findLatestSellClosedOffering(offerings);

        if (sellClosedOffering) return sellClosedOffering;

        const now = serverTime();

        const finalizedOffering = findLatestFinalizedOffering(offerings);

        if (finalizedOffering) {
            const latestMatchStartTime = dayjs.max(
                finalizedOffering.matches.map(({startTime}) => dayjs(startTime)),
            );

            // show offering until 1AM the day after the last match is completed
            const hideOfferingTime = latestMatchStartTime?.add(1, "day").hour(1);

            if (now.isBefore(hideOfferingTime)) {
                return finalizedOffering;
            }
        }

        const sellOpenOfferings = findClosestSellOpenOffering(offerings);

        return sellOpenOfferings ?? finalizedOffering;
    });

export const getOverviewOffering = (
    type?: Pool | GameType,
    sport?: PoolSports | string,
) =>
    createSelector(getOfferingsStateByType(type, sport), ({allIds, byId}) => {
        const offerings = sortOfferings(allIds, byId);

        if (!offerings) return null;

        const latestSellClosedOffering = findLatestSellClosedOffering(offerings);

        if (latestSellClosedOffering) return latestSellClosedOffering;

        const closestSellOpenOffering = findClosestSellOpenOffering(offerings);

        if (closestSellOpenOffering) return closestSellOpenOffering;

        const latestFinalizedOffering = findLatestFinalizedOffering(offerings);

        return latestFinalizedOffering ?? null;
    });

const getClosestOfferingByStartTime = (
    offerings: OfferingWithMatchRefs[],
    compareTime: dayjs.Dayjs,
) => {
    if (!offerings.length) return null;
    return offerings.reduce<OfferingWithMatchRefs | null>((prev, curr) => {
        if (!prev) return curr;
        const currTime = dayjs(curr.startTime);
        const prevTime = dayjs(prev.startTime);

        return Math.abs(currTime.diff(compareTime)) < Math.abs(prevTime.diff(compareTime))
            ? curr
            : prev;
    }, null);
};

/**
 * Selector to get the current offering based on the router location and the state of offerings.
 *
 * Steps:
 * 1. Retrieve offering ID from router state.
 * 2. Check if offering ID exists in offerings state.
 * 3. If exists, return the offering.
 * 4. Sort offerings if ID doesn't exist.
 * 5. Return latest finalized offering if pathname ends with "/resultat".
 * 6. Return latest sell-closed offering if pathname ends with "/live".
 * 7. Return closest sell-open offering or first sorted offering.
 *
 * Uses getActiveOfferingState instead of getOfferingState.
 *
 * @param state - The global state containing the router and offerings information.
 * @returns The current offering based on the router location and offerings state.
 */
export const getOffering = createSelector(
    (state: GlobalBig9State) => state.router?.location,
    getOfferingsState,
    (location, {allIds, byId}) => {
        const typeFromPathname = getGameType(location?.pathname);
        const sportFromPathname = getSport(location?.pathname)?.systemName;

        const sortedOfferings = sortOfferings(allIds, byId);

        if (!sortedOfferings) return null;

        const offerings = getOfferingsByTypeAndSport(
            sortedOfferings,
            typeFromPathname,
            sportFromPathname,
        );

        const offeringIdFromState = (location?.state as Big9RouteState)?.offeringId;

        const offeringFromState = offeringIdFromState
            ? byId[offeringIdFromState]
            : undefined;
        const matchesTypeFromPath =
            !typeFromPathname ||
            (offeringFromState
                ? matchesGameType(offeringFromState.gameType, typeFromPathname)
                : false);
        const matchesSportFromPath =
            !sportFromPathname || offeringFromState?.sport === sportFromPathname;

        if (offeringFromState && matchesTypeFromPath && matchesSportFromPath) {
            return offeringFromState;
        }

        const useFinalizedOffering = location?.pathname?.endsWith("/resultat");

        if (useFinalizedOffering) {
            const latestFinalizedOffering = findLatestFinalizedOffering(offerings);

            if (latestFinalizedOffering) return latestFinalizedOffering;
        }

        const useSellClosedOffering = location?.pathname?.endsWith("/live");

        if (useSellClosedOffering) {
            const latestSellClosedOffering = findLatestSellClosedOffering(offerings);

            if (latestSellClosedOffering) return latestSellClosedOffering;
        }

        const closestSellOpenOffering = findClosestSellOpenOffering(offerings);

        return closestSellOpenOffering ?? offerings[0];
    },
);

export const getQuicklinkOffering = (
    poolType: Pool,
    poolSport?: PoolSports,
): Selector<GlobalBig9State, OfferingWithMatchRefs | null> =>
    createSelector(
        (state: GlobalBig9State) => state.router?.location?.pathname,
        getOfferingsStateByType(poolType, poolSport),
        (pathname, offerings) => {
            const sortedOfferings = sortOfferings(offerings.allIds, offerings.byId);

            if (!sortedOfferings) return null;

            const useFinalizedOffering = pathname?.endsWith("/resultat");

            if (useFinalizedOffering) {
                const latestFinalizedOffering =
                    findLatestFinalizedOffering(sortedOfferings);
                if (latestFinalizedOffering) return latestFinalizedOffering;
            }

            const useSellClosedOffering = pathname?.endsWith("/live");

            if (useSellClosedOffering) {
                const latestSellClosedOffering =
                    findLatestSellClosedOffering(sortedOfferings);
                if (latestSellClosedOffering) return latestSellClosedOffering;
            }

            const closestSellOpenOffering = findClosestSellOpenOffering(sortedOfferings);

            return closestSellOpenOffering ?? sortedOfferings[0];
        },
    );

export const createOfferingSelector = (
    offeringId: string,
): Selector<GlobalBig9State, OfferingWithMatchRefs | null> =>
    createSelector(getOfferingsById, (offerings) => offerings[offeringId]);

export const getOfferingWithMatches = memoize(
    (offeringId: string): Selector<GlobalBig9State, OfferingWithMatches | null> =>
        createSelector(
            createOfferingSelector(offeringId),
            getMatchesState,
            (offering, matches) => {
                if (!offering) return null;
                const {overUnderLimit} = offering;
                return {
                    ...offering,
                    matches: offering.matches.map(({id, number}) => {
                        const match = matches[id];
                        return {
                            ...match,
                            matchId: id,
                            number,
                            overUnderLimit,
                            homeTeamName: match.homeTeam.shortName,
                            homeTeamAbbreviation: match.homeTeam.abbreviation,
                            awayTeamName: match.awayTeam.shortName,
                            awayTeamAbbreviation: match.awayTeam.abbreviation,
                        };
                    }),
                };
            },
        ),
);

// Exported for reuse in /jackpot/start
export const findCurrentOffering = (allIds?: string[], byId?: OfferingsById) => {
    if (!allIds || !byId) return null;

    const offerings = orderBy(
        allIds.map((id) => byId[id]),
        ["startTime", "serialNumber"],
        ["desc", "desc"],
    ).filter((offering) => offering.status !== OfferingStatus.UPCOMING);

    return (offerings && offerings[0]) ?? null;
};

export const getCurrentOffering = createSelector(
    getOfferingIds,
    getOfferingsById,
    findCurrentOffering,
);

export const getOfferingId = createSelector(
    getOffering,
    (offering) => (offering && offering?.id) || "",
);

// export const getCurrentOfferingId = createSelector(
//     getCurrentOffering,
//     (offering) => (offering)?.id ?? null,
// );

// export const getOverUnderLimit = (state: GlobalBig9State, offeringId: string): number =>
//     state.sportsbook.big9.offerings.byId[offeringId]?.overUnderLimit;

export const getOfferingStatus: (
    offeringId: string,
) => (state: GlobalBig9State) => OfferingStatus | null =
    (offeringId: string) => (state: GlobalBig9State) =>
        createOfferingSelector(offeringId)(state)?.status ?? null;

// export const getOfferingStartTime: (
//     offeringId: string,
// ) => (state: GlobalBig9State) => string | null =
//     (offeringId: string) => (state: GlobalBig9State) =>
//         createOfferingSelector(offeringId)(state)?.startTime ?? null;

export const getOfferingJackpot: (
    offeringId?: string,
) => (state: GlobalBig9State) => number | null =
    (offeringId?: string) => (state: GlobalBig9State) => {
        if (!offeringId) return null;

        const offering = createOfferingSelector(offeringId)(state);
        const expectedJackpot = offering?.pool?.expectedJackpot;
        return expectedJackpot ? expectedJackpot / 100 : null;
    };

export const isOfferingsLoading = (state: GlobalBig9State) =>
    fetchSelectors.isLoading(getOfferingsState(state));
export const isOfferingsLoaded = (state: GlobalBig9State) =>
    fetchSelectors.isLoaded(getOfferingsState(state));
export const hasOfferingsError = (state: GlobalBig9State) =>
    fetchSelectors.hasError(getOfferingsState(state));

export const createMostPlayedSelector = (
    offeringId: string,
): Selector<GlobalBig9State, MostPlayed | null> =>
    createSelector(getOfferingsByIdWithMatches, (offering) => {
        if (!offering[offeringId]) return null;
        const {matches} = offering[offeringId];

        const matchValues = Object.values(matches);

        if (!matchValues.some((match) => match.betDistribution)) return null;

        const matchesMap = matchValues.map(
            ({betDistribution, homeTeam, awayTeam, number}) => {
                const {wildcard, ...rest} = betDistribution ?? {};
                // Most played fulltime result of match
                const winner = Object.entries(rest).reduce(
                    (acc, [key, value]) =>
                        value.over + value.under > acc.percentage
                            ? {
                                  percentage: value.over + value.under,
                                  selection: key as MatchResult["winner"],
                              }
                            : acc,
                    {
                        percentage: 0,
                        selection: BetSelection.Home as MatchResult["winner"],
                    },
                );
                // Most played over/under outcome of match
                const overUnder = Object.values(rest).reduce(
                    (acc, value) => ({
                        over: {...acc.over, percentage: acc.over.percentage + value.over},
                        under: {
                            ...acc.under,
                            percentage: acc.under.percentage + value.under,
                        },
                    }),
                    {
                        over: {
                            percentage: 0,
                            selection: BetSelection.Over as MatchResult["goals"],
                        },
                        under: {
                            percentage: 0,
                            selection: BetSelection.Under as MatchResult["goals"],
                        },
                    },
                );
                const goal: {percentage: number; selection: MatchResult["goals"]} =
                    overUnder.over.percentage > overUnder.under.percentage
                        ? overUnder.over
                        : overUnder.under;
                return {winner, goal, wildcard, homeTeam, awayTeam, matchNumber: number};
            },
        );
        const winners = orderBy(matchesMap, (match) => match.winner.percentage, ["desc"])
            .slice(0, 2)
            .map(
                ({winner: {percentage, ...winner}, homeTeam, awayTeam, matchNumber}) => ({
                    ...winner,
                    percentage: Math.round(percentage),
                    homeTeam,
                    awayTeam,
                    matchNumber,
                }),
            );
        const goals = orderBy(matchesMap, (match) => match.goal.percentage, ["desc"])
            .slice(0, 2)
            .map(({goal: {percentage, ...goal}, homeTeam, awayTeam, matchNumber}) => ({
                ...goal,
                percentage: Math.round(percentage),
                homeTeam,
                awayTeam,
                matchNumber,
            }));
        const wildcards = orderBy(matchesMap, (match) => match.wildcard, ["desc"])
            .slice(0, 2)
            .map(({homeTeam, awayTeam, matchNumber, wildcard}) => ({
                percentage: Math.round(wildcard ?? 0),
                homeTeam,
                awayTeam,
                matchNumber,
            }));

        return {winners, goals, wildcards};
    });

// APP SPECIFIC SELECTORS
export const getCurrentOfferingCanceledOrPaused = (): Selector<
    GlobalBig9State,
    boolean
> =>
    createSelector(getCurrentOffering, (offering) =>
        [OfferingStatus.CANCELED, OfferingStatus.UNDER_REVIEW].includes(
            offering?.status || ("" as OfferingStatus),
        ),
    );

export const getOfferingCanceledOrPaused =
    (offeringId: string) => (state: GlobalBig9State) => {
        const offering = createOfferingSelector(offeringId)(state);
        return [OfferingStatus.CANCELED, OfferingStatus.UNDER_REVIEW].includes(
            offering?.status || ("" as OfferingStatus),
        );
    };

const getFinalizedAndCanceledOfferingsByType = (
    type: Pool | GameType | undefined,
    sport: PoolSports | string | undefined,
) =>
    createSelector(getOfferingsStateByType(type, sport), (offerings) =>
        orderBy(
            offerings.allIds
                .map((offeringId) => offerings.byId[offeringId])
                .filter(getIsFinalizedOrCanceledOffering),
            ["startTime", "serialNumber"],
            ["desc", "desc"],
        ),
    );

export const getAllExceptUpcomingOfferings = (
    type?: Pool | GameType,
    sport?: PoolSports | string,
) =>
    createSelector(
        getOfferingsStateByType(type, sport),
        getFinalizedAndCanceledOfferingsByType(type, sport),
        (offerings, finalizedOrCanceledOfferings) => {
            const allExceptFinalizedOrCanceledOfferings = offerings.allIds
                .map((offeringId) => offerings.byId[offeringId])
                .filter(
                    (offering): boolean => !getIsFinalizedOrCanceledOffering(offering),
                );

            const allExceptUpcomingOfferings = orderBy(
                [
                    ...allExceptFinalizedOrCanceledOfferings,
                    ...finalizedOrCanceledOfferings,
                ].filter(
                    (offering): boolean => offering?.status !== OfferingStatus.UPCOMING,
                ),
                ["startTime", "serialNumber"],
                ["desc", "desc"],
            );

            return allExceptUpcomingOfferings;
        },
    );

export const getDefaultOffering = (
    type: Pool | undefined,
    sport?: PoolSports | undefined,
) =>
    createSelector(getAllExceptUpcomingOfferings(type, sport), (offerings) => {
        if (!offerings) return null;

        const now = serverTime();

        const closestLiveOffering = getClosestOfferingByStartTime(
            offerings.filter((offering) =>
                liveOfferingStatuses.includes(offering.status),
            ),
            now,
        );

        if (closestLiveOffering) {
            return closestLiveOffering;
        }

        const sellOpenOfferings = offerings.filter((offering) =>
            preLiveOfferingStatuses.includes(offering.status),
        );

        const closestFinalizedOffering = getClosestOfferingByStartTime(
            offerings.filter((offering) => offering.status === OfferingStatus.FINALIZED),
            now,
        );

        if (sellOpenOfferings.length <= 1) {
            if (closestFinalizedOffering) {
                const latestMatchStartTime = dayjs.max(
                    closestFinalizedOffering.matches.map(({startTime}) =>
                        dayjs(startTime),
                    ),
                );

                const hideOfferingTime = latestMatchStartTime?.add(1, "day").hour(1);

                if (now.isBefore(hideOfferingTime)) {
                    return closestFinalizedOffering;
                }
            }
        }

        const closestOpenOffering = getClosestOfferingByStartTime(sellOpenOfferings, now);

        if (closestOpenOffering) {
            return closestOpenOffering;
        }

        return closestFinalizedOffering;
    });

export const getLatestResultOfferingsByType = (
    type: Pool | undefined,
    sport?: PoolSports | undefined,
) =>
    createSelector(getOfferingsStateByType(type, sport), (offerings) =>
        orderBy(
            offerings.allIds
                .map((offeringId) => offerings.byId[offeringId])
                .filter(getHasOfferingPassed),
            ["startTime", "serialNumber"],
            ["desc", "desc"],
        ).slice(0, 3),
    );

export const getOngoingOfferingsByType = (
    type?: Pool | GameType | undefined,
    sport?: PoolSports | undefined,
) =>
    createSelector(getOfferingsStateByType(type, sport), (offerings) => {
        const ongoingOfferings = orderBy(
            offerings.allIds
                .map((offeringId) => offerings.byId[offeringId])
                .filter(
                    (offering: OfferingWithMatchRefs | null): boolean =>
                        offering !== null &&
                        preLiveOfferingStatuses.includes(offering.status),
                ),
            ["startTime", "serialNumber"],
            ["desc", "desc"],
        );

        return ongoingOfferings.length === 0 ? null : ongoingOfferings;
    });

export const calendarOfferingsByType = (
    type?: Pool | GameType | undefined,
    sport?: PoolSports | undefined,
) =>
    createSelector(getOfferingsStateByType(type, sport), (offerings) => {
        const offeringsToMap = offerings.allIds.map(
            (offeringId) => offerings.byId[offeringId],
        );

        const allExceptUpcomingOfferings = orderBy(
            offeringsToMap.filter(
                (offering): boolean => offering?.status !== OfferingStatus.UPCOMING,
            ),
            ["startTime", "serialNumber"],
            ["desc", "desc"],
        );

        return allExceptUpcomingOfferings.slice(0, type === Pool.Big9 ? 5 : 10);
    });

export const getOfferingTypeAndSport = (offeringId: string) =>
    createSelector(getOfferingById(offeringId), (offering) => {
        if (!offering) return {poolType: null, poolSport: null};

        const {gameType, sport} = offering;

        return {
            poolType: gameType,
            poolSport: sport,
        };
    });
